import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateChannelContributions, UpdateChannelContributions } from '@repo/backend/crt'
import {
  CHANNEL_CONTRIBUTIONS_CREATE_ERROR_TYPES,
  MONTH_KEYS,
  PRODUCT_CATEGORIES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const channelContributionsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const CHANNEL_CONTRIBUTIONS_YEAR_VALUES = ['cy', 'ny'] as const
export type ChannelContributionsYear = (typeof CHANNEL_CONTRIBUTIONS_YEAR_VALUES)[number]

export const channelContributionsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  pricingSubChannelValue: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  month: z.enum(MONTH_KEYS).optional(),
  year: z.enum(CHANNEL_CONTRIBUTIONS_YEAR_VALUES).optional(),
  ...datatableSearchSchema.shape,
})

export type ChannelContributionsSearch = z.infer<typeof channelContributionsSearchSchema>
export type ChannelContributionsDetailsSearch = z.infer<
  typeof channelContributionsDetailsSearchSchema
>

export type ChannelContributionsDetailsServerQuery = Omit<
  ChannelContributionsDetailsSearch,
  'month' | 'year'
>

export type ChannelContributionsList = Awaited<ReturnType<typeof getChannelContributionsList>>
export type ChannelContributions = ChannelContributionsList['results'][number]
export type ChannelContributionsWithDetails = Awaited<
  ReturnType<typeof getChannelContributionsById>
>

type UseCreateChannelContributionsArgs = {
  queries: ChannelContributionsSearch
  handleOnSuccess: (channelContributionId: number) => void
}

type UseUpdateChannelContributionsArgs = QueryHandlers & {
  channelContributionId: number
}

type UseDeleteChannelContributionsArgs = QueryHandlers & {
  channelContributionIds: number[]
  queries: ChannelContributionsSearch
}

async function getChannelContributionsList(search: ChannelContributionsSearch) {
  const api = getApi()
  const res = await api.crt['channel-contributions'].$get({
    query: stringifyValues(search),
  })

  return await res.json()
}

async function getCurrentChannelContributions(crtPeriod?: string) {
  const api = getApi()
  const res = await api.crt['channel-contributions'].current.$get({
    query: stringifyValues({ crtPeriod }),
  })
  return await res.json()
}

export const currentChannelContributionsQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentChannelContributions', crtPeriod ?? null] as const,
  queryFn: () => getCurrentChannelContributions(crtPeriod),
})

export function useCurrentChannelContributions(crtPeriod?: string) {
  return useQuery(currentChannelContributionsQueryOptions(crtPeriod))
}

async function getChannelContributionsById(
  id: string,
  queries: ChannelContributionsDetailsServerQuery = {},
) {
  const api = getApi()
  const res = await api.crt['channel-contributions'][':id'].$get({
    param: { id },
    query: stringifyValues(queries),
  })

  return await res.json()
}

async function createChannelContributions(data: CreateChannelContributions) {
  const api = getApi()

  const res = await api.crt['channel-contributions'].$post({
    json: data,
  })

  return await res.json()
}

async function updateChannelContributions(
  channelContributionId: number,
  details: UpdateChannelContributions,
) {
  const api = getApi()

  await api.crt['channel-contributions'][':id'].$patch({
    param: { id: String(channelContributionId) },
    json: details,
  })
}

async function deleteChannelContributions(channelContributionIds: number[]) {
  const api = getApi()

  await api.crt['channel-contributions'].bulk.$delete({
    json: { ids: channelContributionIds },
  })
}

export function useChannelContributionsList(queries: ChannelContributionsSearch) {
  return useQuery({
    queryKey: ['channelContributions', queries],
    queryFn: () => getChannelContributionsList(queries),
  })
}

export const channelContributionsByIdQueryOptions = (
  id: string,
  queries: ChannelContributionsDetailsServerQuery = {},
) => ({
  queryKey: ['channelContributionsById', id, queries],
  queryFn: () => getChannelContributionsById(id, queries),
})

export function useChannelContributionsById(
  id: string,
  queries: ChannelContributionsDetailsServerQuery = {},
) {
  return useQuery(channelContributionsByIdQueryOptions(id, queries))
}

async function getChannelContributionsDetailsForExport(
  id: string,
  queries: ChannelContributionsDetailsServerQuery = {},
) {
  const stringifiedQueries = stringifyValues(queries)

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/channel-contributions/${id}/details/export?${queryParams}`,
  )
  return csvExport
}

export const channelContributionsDetailsExportQueryOptions = (
  id: string,
  queries: ChannelContributionsDetailsServerQuery = {},
) => ({
  queryKey: ['channelContributionsDetailsExport', id, queries],
  queryFn: () => getChannelContributionsDetailsForExport(id, queries),
})

async function getChannelContributionsDetailsForRawExport(id: string) {
  const csvExport = await fetchCsvExport(`/crt/channel-contributions/${id}/details/raw-export`)
  return csvExport
}

export const channelContributionsDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['channelContributionsDetailsRawExport', id],
  queryFn: () => getChannelContributionsDetailsForRawExport(id),
})

export function useCreateChannelContributions({
  queries,
  handleOnSuccess,
}: UseCreateChannelContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['channelContributions', queries],
    mutationFn: createChannelContributions,
    onSuccess: (channelContribution) => {
      handleOnSuccess(channelContribution.id)

      queryClient.invalidateQueries({
        queryKey: ['channelContributions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelContributions.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case CHANNEL_CONTRIBUTIONS_CREATE_ERROR_TYPES.crtPeriodConflict:
          description = `${t('errors.channelContributions.createFailed', { ns: 'crt' })}: ${t(
            'errors.channelContributions.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(
            issues,
            t,
            'errors.channelContributions.createFailed',
            'crt',
          )
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateChannelContributions({
  channelContributionId,
  handleOnSuccess,
}: UseUpdateChannelContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateChannelContributions', channelContributionId],
    mutationFn: (details: UpdateChannelContributions) =>
      updateChannelContributions(channelContributionId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['channelContributionsById', String(channelContributionId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelContributions.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.channelContributions.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteChannelContributions({
  channelContributionIds,
  queries,
  handleOnSuccess,
}: UseDeleteChannelContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteChannelContributions'],
    mutationFn: () => deleteChannelContributions(channelContributionIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['channelContributions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelContributions.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.channelContributions.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
